import { Cell, Column, Row, Table } from '@tanstack/react-table';
import { ColumnHeader, ConfigOptions, download, generateCsv, mkConfig } from 'export-to-csv';
import { FormattedMessage, useIntl } from 'react-intl';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { useDownloadProgress } from 'hooks/useDownloadProgress';
import { ColumnMeta } from 'components/table/DataTable';
import { Tooltip } from 'components/Tooltip';
import { Button } from 'components/Button';

interface DataTableExportButtonProps<T> {
  table: Table<T>;
  config: DataTableExportConfigOptions;
  disabled?: boolean;
}

export function DataTableExportButton<T>({ table, config, disabled = false }: DataTableExportButtonProps<T>) {
  const intl = useIntl();
  const { withDownloadProgress } = useDownloadProgress();

  const getHeaderExporterForCell = <T,>(column: Column<T, unknown>) => {
    if (config.customHeaderExporters?.[column.id]) {
      return config.customHeaderExporters?.[column.id];
    }
    const meta = column.columnDef.meta ? (column.columnDef.meta as ColumnMeta) : undefined;
    return meta?.headerExporter;
  };

  const isSkippedColumn = <T,>(column: Column<T, unknown>) => {
    if (config.skippedColumns?.includes(column.id)) {
      return true;
    }
    const meta = column.columnDef.meta ? (column.columnDef.meta as ColumnMeta) : undefined;
    return !!meta?.skipExport;
  };

  const extractHeaders = () => {
    return table
      .getHeaderGroups()
      .flatMap(h =>
        h.headers.map(header => {
          const columnId = header.column.id;

          if (header.isPlaceholder || header.subHeaders.length > 0) {
            return undefined;
          }

          if (isSkippedColumn(header.column)) {
            return undefined;
          }

          const headerExporter = getHeaderExporterForCell(header.column);
          if (headerExporter) {
            return { key: columnId, displayLabel: headerExporter(intl) };
          }

          const columnHeader = header.column.columnDef.header;
          if (typeof columnHeader === 'function') {
            const headerValue = columnHeader(header.getContext()) as string;
            if (headerValue) {
              return { key: columnId, displayLabel: headerValue };
            }
          }
          return { key: columnId, displayLabel: columnHeader } as ColumnHeader;
        })
      )
      .filter(header => header !== undefined);
  };

  const getExportConfigForCell = <T,>(cell: Cell<T, unknown>) => {
    if (config.customCellValueExporters?.[cell.column.id]) {
      return config.customCellValueExporters[cell.column.id];
    }
    const meta = cell.column.columnDef.meta ? (cell.column.columnDef.meta as ColumnMeta) : undefined;
    return meta?.valueExporter;
  };

  const parseRow = <T,>(row: Row<T>) => {
    const rowData: Record<string, string> = {};
    row.getVisibleCells().forEach(cell => {
      const cellValue = cell.getValue();
      const columnId = cell.column.id;

      if (config.skippedColumns?.includes(columnId)) {
        return;
      }

      const valueExporter = getExportConfigForCell(cell);
      if (valueExporter) {
        rowData[columnId] = valueExporter(cellValue, intl);
        return;
      }

      if (typeof cellValue === 'object' && cellValue !== null) {
        rowData[columnId] = cellValue.toString();
      } else {
        rowData[columnId] = cellValue as string;
      }
    });

    return rowData;
  };

  const extractRowData = () => {
    return table.getExpandedRowModel().rows.flatMap(row => {
      const rowData: Record<string, string>[] = [];
      rowData.push(parseRow(row));

      if (row.getIsExpanded()) {
        row.getLeafRows().forEach(leafRow => {
          rowData.push(parseRow(leafRow));
        });
      }

      return rowData;
    });
  };

  const exportCsv = async () => {
    await withDownloadProgress(() => {
      const configOptions: ConfigOptions = {
        fieldSeparator: intl.locale === 'de-DE' ? ';' : ',',
        filename: config.exportName,
        showColumnHeaders: true,
      };

      configOptions.columnHeaders = extractHeaders();

      const rows = extractRowData();

      const csvConfig = mkConfig(configOptions);
      const csv = generateCsv(csvConfig)(rows);
      download(csvConfig)(csv);

      return Promise.resolve(undefined);
    });
  };

  return (
    <div>
      <Tooltip tooltipContent={<FormattedMessage id="insights.report.download" />}>
        <Button icon="Download" onClick={exportCsv} disabled={disabled} />
      </Tooltip>
    </div>
  );
}
